import React, { useState, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';

const LogSlider = ({name, label, tooltip, value, step, min, max, onChange}) => {
  const eps = 1e-12;
  const transform = (val) => Math.round(10**-val*1e10)/1e10;

  const backTransform = (val) => -Math.log10(val);

  return <div className="mb-3 ">
    <label
      htmlFor={name}
      className="form-label"
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltip}
    >{label}</label>
    <input
      className="form-range"
      type="range"
      id={name}
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={e => onChange(e.target.value)}
    />
    <div className="input-group">
      <label htmlFor={name} className="input-group-text">{name}</label>
      <input
        className="form-control"
        type="number"
        id={name}
        step={1e-10}
        value={(max - value <= eps) ? 0 : transform(value)}
        max={transform(min)}
        min={transform(max)}
        onChange={e => onChange(backTransform(e.target.value))}
      />
    </div>
  </div>
}

const Slider = ({name, label, tooltip, value, step, min, max, onChange}) => (
  <div className="mb-3 ">
    <label
      htmlFor={name}
      className="form-label"
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltip}
    >{label}</label>
    <input
      className="form-range"
      type="range"
      value={value} id={name}
      step={step}
      min={min}
      max={max}
      onChange={e => onChange(e.target.value)}
    />
    <div className="input-group">
      <label htmlFor={name} className="input-group-text">{name}</label>
      <input
        className="form-control"
        type="number"
        value={value}
        id={name}
        step={step}
        min={min}
        max={max}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  </div>
)

export const OptionalParameters = ( { pFtol, setPFtol, pGtol, setPGtol, tolMax, maxIter, setMaxIter, mode, setMode } ) => {
  return <>
    <div className="mb-3">
    </div>
    <div className="mb-3">
      <div className="form-check form-check-inline form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id="mode-checkbox"
          checked={mode == 'guess'}
          onChange={() => mode == 'guess' ? setMode('naive') : setMode('guess')}
        />
        <label
          className="form-check-label"
          for="mode-checkbox"
          data-tooltip-id="my-tooltip"
          data-tooltip-html="<p>If unchecked, all fatty acids are initialized with the same probability.</p>"
        >Guess start profile</label>
      </div>
    </div>
    <LogSlider
      name="ftol"
      label="absolute sensitivity"
      value={pFtol}
      step={1}
      min={1}
      max={tolMax}
      onChange={setPFtol}
      tooltip={" \
        <p>A higher absolute sensitivity corresponds to a lower ftol, which leads to longer computation times.<p> \
        <p>If the smallest absolute change of fatty acid probability between optimization steps is below ftol, the optimization algorithm stops.</p> \
      "}
      />
    <LogSlider
      name='gtol'
      label='relative sensitivity'
      value={pGtol}
      step={1}
      min={1}
      max={tolMax}
      onChange={setPGtol}
      tooltip={" \
        <p>A higher relative sensitivity corresponds to a lower gtol, which leads to longer computation times.<p> \
        <p>If the smallest relative change of fatty acid probability between optimization steps is below gtol, the optimization algorithm stops.</p> \
      "}
      />
    <Slider
      name="max_iter"
      label="maximal optimization steps"
      value={maxIter}
      min={1}
      max={300}
      step={1}
      onChange={setMaxIter}
      tooltip="The optimization may stop before max_iter is reached even if ftol and gtol are set to 0."
      />
    <Tooltip id="my-tooltip" />
  </>
};


const CategoricalSlider = ({categories, value, onChange}) => {
  return <div className="mb-3 ">
    <label
      htmlFor="categorical-slider"
      className="form-label"
    >Pre-defined optimization parameters</label>
    <input
      className="form-range"
      type="range"
      value={value}
      min={0}
      max={categories.length-1}
      step={1}
      id="categorical-slider"
      onChange={e => onChange(e.target.value)}
    />
    <div className="input-group">
      <label htmlFor="categorical-display" className="input-group-text">
        Performance
      </label>
      <input
        className="form-control"
        type="text"
        value={categories.at(value)}
        id="categorical-display"
        step={1}
        min={0}
        max={categories.length}
        readOnly
        disabled
      />
    </div>
  </div>
}

export const EasyOptionalParameters = ({pFtol, pGtol, maxIter, mode, onChange, tolMax}) => {
  const categories = [
    {name: "draft", params: {pFtol: 4, pGtol: 4, maxIter: 30, mode: "guess"}},
    {name: "fast", params: {pFtol: 6, pGtol: 4, maxIter: 200, mode: "guess"}},
    {name: "thorough", params: {pFtol: 10, pGtol: 8, maxIter: 300, mode: "guess"}},
    {name: "high accuracy", params: {pFtol: tolMax, pGtol: tolMax, maxIter: 300, mode: "naive"}},
  ]
  const updateDisplayValue = () => {
    if ((pFtol >= tolMax) && (pGtol >= tolMax) && (mode === 'naive')){
      return 3
    } else if ((pFtol  >= 7) && (pGtol >= 7) && (maxIter >= 50)) {
      return 2
    } else if ((pFtol >= 5) && (pGtol >= 3) && (maxIter >= 30)) {
      return 1
    }
    return 0
  }
  const [displayValue, setDisplayValue] = useState(updateDisplayValue());

  useEffect(() => setDisplayValue(updateDisplayValue()), [pFtol, pGtol, maxIter, mode])

  return <CategoricalSlider
    categories={categories.map(c => c.name)}
    value={displayValue}
    onChange={(i) => {setDisplayValue(i); onChange(categories.at(i).params)}}
  />
};
